import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiClient, type AuthResponse } from '../services/apiClient';
import { baleWebApp } from '../services/baleWebApp';
import { mockUserData } from '../data/mockData';

interface AuthContextType {
  isAuthenticated: boolean;
  isBackendAvailable: boolean;
  isDemo: boolean;
  isLoading: boolean;
  userId: string | null;
  baleUserId: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isBackendAvailable, setIsBackendAvailable] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [baleUserId, setBaleUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);

  const clearUserData = useCallback((): void => {
    setUserId(null);
    setBaleUserId(null);
    setUsername(null);
    setFirstName(null);
    setLastName(null);
  }, []);

  const applyAuthResponse = useCallback((response: AuthResponse): void => {
    apiClient.setToken(response.token);
    setUserId(response.user_id);
    setBaleUserId(response.bale_user_id);
    setUsername(response.username || null);
    setFirstName(response.first_name || null);
    setLastName(response.last_name || null);
    setIsAuthenticated(true);
    setIsBackendAvailable(true);
    setIsDemo(false);
  }, []);

  const applyDemoAuth = useCallback((): void => {
    setUserId(mockUserData.user_id);
    setBaleUserId(mockUserData.bale_user_id);
    setUsername(mockUserData.username);
    setFirstName(mockUserData.first_name);
    setLastName(mockUserData.last_name);
    setIsAuthenticated(true);
    setIsBackendAvailable(false);
    setIsDemo(true);
  }, []);

  const exchangeBaleInitData = useCallback(async (): Promise<boolean> => {
    const initData = baleWebApp.getInitData();
    if (!initData) {
      applyDemoAuth();
      return false;
    }
    try {
      const response: AuthResponse = await apiClient.exchangeInitData(initData);
      applyAuthResponse(response);
      return true;
    } catch {
      applyDemoAuth();
      return false;
    }
  }, [applyAuthResponse, applyDemoAuth]);

  const login = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      await exchangeBaleInitData();
    } catch (error) {
      console.error('Login failed:', error);
      applyDemoAuth();
    } finally {
      setIsLoading(false);
    }
  }, [exchangeBaleInitData, applyDemoAuth]);

  const checkAuth = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const token = apiClient.getToken();

      if (token) {
        const result = await apiClient.validateToken();
        if (result.valid) {
          setIsAuthenticated(true);
          setIsBackendAvailable(true);
          setIsDemo(false);
          setUserId(result.user_id || null);
          return;
        }
      }

      apiClient.clearToken();
      await exchangeBaleInitData();
    } catch (error) {
      console.error('Auth check failed:', error);
      apiClient.clearToken();
      applyDemoAuth();
    } finally {
      setIsLoading(false);
    }
  }, [exchangeBaleInitData, applyDemoAuth]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      await apiClient.revokeToken();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      apiClient.clearToken();
      setIsAuthenticated(false);
      setIsBackendAvailable(false);
      setIsDemo(false);
      clearUserData();
      setIsLoading(false);
    }
  }, [clearUserData]);

  const value: AuthContextType = {
    isAuthenticated,
    isBackendAvailable,
    isDemo,
    isLoading,
    userId,
    baleUserId,
    username,
    firstName,
    lastName,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
